export type CatalogKind = "service" | "package" | "addon";

export interface CatalogItem {
  id: string;
  name: string;
  kind: CatalogKind;
  price: number;
  priceLabel?: string;
  mostPopular?: boolean;
  category: string;
  includes: string[];
  description: string;
  serviceTags: string[];
}

export const serviceCatalog: CatalogItem[] = [
  service("rsvp-essential", "RSVP Hub Essential", 499, "RSVP Hub", ["RSVP collection", "Guest count tracking", "Event details", "Countdown timer", "Maps & directions", "Registry, donation, menu, and dietary links", "Guest Communication Center", "Professional custom design"], ["rsvp"]),
  service("rsvp-signature", "RSVP Hub Signature", 699, "RSVP Hub", ["Everything in Essential", "Enhanced layout options", "Expanded guest preferences", "Premium RSVP presentation"], ["rsvp"]),
  service("rsvp-legacy", "RSVP Hub Legacy", 999, "RSVP Hub", ["Everything in Signature", "Legacy guest experience support", "Expanded personalization", "Premium launch-ready design"], ["rsvp"]),
  service("memory-essential", "Memory Hub Essential", 599, "Memory Hub", ["Up to 28 total memories", "Suggested mix: up to 25 photos and 3 videos", "Custom Memory Hub design", "Memory Media Vault not available"], ["memory"]),
  service("memory-signature", "Memory Hub Signature", 899, "Memory Hub", ["Up to 45 total memories", "Featured memories", "Premium layouts", "Tribute section", "Enhanced design personalization", "Memory Media Vault available as add-on"], ["memory"]),
  service("memory-legacy", "Memory Hub Legacy", 1299, "Memory Hub", ["Up to 66 total memories", "Suggested mix: up to 60 photos and 6 videos", "Premium layouts", "Memory Media Vault included"], ["memory", "vault"]),
  service("memory-media-vault", "Memory Media Vault", 299, "Memory Media Vault", ["First vault setup", "Guest upload link", "Client portal access", "Pending review queue", "Approved Vault Gallery"], ["vault"]),
  service("additional-event-vault", "Additional Event Vault", 149, "Memory Media Vault", ["Additional event vault setup", "Event-specific upload organization", "Separate event collection flow"], ["vault"]),
  service("archive-hub", "Archive Hub", 299, "Archive Hub", ["Event directory placement", "Timeline organization", "RSVP Hub linking", "Memory Hub linking", "Basic color personalization"], ["archive"]),
  service("signature-song", "Signature Song", 125, "Signature Keepsakes", ["Custom digital keepsake song", "Client-provided inspiration and story", "Creative delivery for selected event or honoree"], ["song"]),
  service("signature-highlight-reel", "Signature Highlight Reel", 399, "Signature Keepsakes", ["Short-form keepsake video", "Selected photos, videos, music, captions, and memories", "Digital delivery"], ["highlight"]),
  addon("celebration-effects", "Celebration Effects Package", 99, "Experience Enhancements", ["Themed motion", "Confetti, petals, glow effects", "Celebratory transitions"], ["enhancement"]),
  addon("cinematic-experience", "Cinematic Experience Package", 199, "Experience Enhancements", ["Cinematic openings", "Luxury transitions", "Mood-setting motion design"], ["enhancement"]),
  addon("signature-custom-experience", "Signature Custom Experience", 299, "Experience Enhancements", ["Custom visual concepts", "Advanced motion or interactive features", "Final price may increase based on scope"], ["enhancement"]),
  service("custom-community-experience", "Custom Community Experience", 1500, "Organization & Community", ["Custom quote based on event complexity", "Guest volume planning", "Upload/storage needs", "Sponsor/vendor sections", "Multi-day schedules"], ["custom"]),
];

export const packageCatalog: CatalogItem[] = [
  pkg("essential-experience", "Essential Experience", 999, ["RSVP Hub Essential", "Memory Hub Essential"], ["rsvp", "memory"]),
  pkg("signature-experience", "Signature Experience", 1499, ["RSVP Hub Signature", "Memory Hub Signature", "Signature Song"], ["rsvp", "memory", "song"]),
  pkg("legacy-experience", "Legacy Experience", 1999, ["RSVP Hub Legacy", "Memory Hub Legacy", "Memory Media Vault", "Archive Hub", "Signature Song", "Premium Design Package"], ["rsvp", "memory", "vault", "archive", "song"], true),
  pkg("legacy-collection", "Legacy Collection", 2599, ["RSVP Hub Legacy", "Memory Hub Legacy", "Memory Media Vault", "Archive Hub", "Signature Song", "Signature Highlight Reel", "Cinematic Experience Package"], ["rsvp", "memory", "vault", "archive", "song", "highlight", "enhancement"]),
  { id: "custom-quote", name: "Custom Quote", kind: "package", price: 0, priceLabel: "Custom", category: "Packages", includes: ["Custom package pricing prepared by Kreatethedate"], description: "Custom quote based on scope, complexity, guest volume, storage needs, and design requirements.", serviceTags: ["custom"] },
];

export const addonCatalog = serviceCatalog.filter((item) => item.kind === "addon");
export const selectableServices = serviceCatalog.filter((item) => item.kind === "service");

export interface PricingInput {
  selectedPackage?: string;
  selectedServices: string[];
  selectedAddons: string[];
  discount?: number;
  customAdjustment?: number;
  customPackagePrice?: number;
  customAddOnPrice?: number;
  paymentOverride?: string;
}

export function calculatePricing(input: PricingInput) {
  const selectedPackage = packageCatalog.find((item) => item.id === input.selectedPackage);
  const packagePrice = selectedPackage?.id === "custom-quote" ? Number(input.customPackagePrice || 0) : selectedPackage?.price || 0;
  const serviceTotal = input.selectedServices.reduce((sum, id) => sum + (selectableServices.find((item) => item.id === id)?.price || 0), 0);
  const addonTotal = input.selectedAddons.reduce((sum, id) => sum + (addonCatalog.find((item) => item.id === id)?.price || 0), 0) + Number(input.customAddOnPrice || 0);
  const discount = Number(input.discount || 0);
  const customAdjustment = Number(input.customAdjustment || 0);
  const total = Math.max(0, packagePrice + serviceTotal + addonTotal - discount + customAdjustment);
  const paymentDueInFull = total < 999;
  const depositDue = paymentDueInFull ? total : Math.round(total / 2);
  const remainingBalance = paymentDueInFull ? 0 : total - depositDue;
  return {
    packagePrice,
    serviceTotal,
    addonTotal,
    discount,
    customAdjustment,
    total,
    depositDue,
    remainingBalance,
    paymentDueInFull,
    paymentTerms: input.paymentOverride || (paymentDueInFull ? "Payment due in full." : "50% deposit required. Remaining balance is due before final delivery or launch."),
  };
}

export function selectedCatalogItems(packageId: string | undefined, services: string[], addons: string[]) {
  return [
    ...packageCatalog.filter((item) => item.id === packageId),
    ...selectableServices.filter((item) => services.includes(item.id)),
    ...addonCatalog.filter((item) => addons.includes(item.id)),
  ];
}

export function serviceTagsForSelection(packageId: string | undefined, services: string[], addons: string[]) {
  return Array.from(new Set(selectedCatalogItems(packageId, services, addons).flatMap((item) => item.serviceTags)));
}

export function formatCurrency(value: number) {
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(value || 0);
}

function service(id: string, name: string, price: number, category: string, includes: string[], serviceTags: string[]): CatalogItem {
  return { id, name, kind: "service", price, category, includes, serviceTags, description: `${name} includes ${includes[0].toLowerCase()} and a polished Kreatethedate client experience.` };
}

function addon(id: string, name: string, price: number, category: string, includes: string[], serviceTags: string[]): CatalogItem {
  return { id, name, kind: "addon", price, priceLabel: id === "signature-custom-experience" ? "$299+" : undefined, category, includes, serviceTags, description: `${name} enhances the selected Kreatethedate experience.` };
}

function pkg(id: string, name: string, price: number, includes: string[], serviceTags: string[], mostPopular = false): CatalogItem {
  return { id, name, kind: "package", price, category: "Packages", includes, serviceTags, mostPopular, description: `${name} bundles Kreatethedate services into one clear client experience.` };
}
